import { supabase } from '../lib/supabase';

export const systemControlService = {
  // ============================================================================
  // ORGANIZATION BRANCHES
  // ============================================================================

  async getAllBranches() {
    const { data, error } = await supabase
      ?.from('organization_branches')
      ?.select('*')
      ?.order('branch_code', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      branchCode: row?.branch_code,
      branchName: row?.branch_name,
      branchType: row?.branch_type,
      address: row?.address,
      city: row?.city,
      province: row?.province,
      postalCode: row?.postal_code,
      phone: row?.phone,
      email: row?.email,
      managerName: row?.manager_name,
      managerEmail: row?.manager_email,
      isActive: row?.is_active,
      createdAt: row?.created_at
    })) || [];
  },

  async createBranch(branchData) {
    const { data, error } = await supabase
      ?.from('organization_branches')
      ?.insert({
        branch_code: branchData?.branchCode,
        branch_name: branchData?.branchName,
        branch_type: branchData?.branchType,
        address: branchData?.address,
        city: branchData?.city,
        province: branchData?.province,
        postal_code: branchData?.postalCode,
        phone: branchData?.phone,
        email: branchData?.email,
        manager_name: branchData?.managerName,
        manager_email: branchData?.managerEmail,
        is_active: branchData?.isActive
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id, branchCode: data?.branch_code };
  },

  async updateBranch(id, branchData) {
    const { data, error } = await supabase
      ?.from('organization_branches')
      ?.update({
        branch_name: branchData?.branchName,
        branch_type: branchData?.branchType,
        address: branchData?.address,
        city: branchData?.city,
        province: branchData?.province,
        postal_code: branchData?.postalCode,
        phone: branchData?.phone,
        email: branchData?.email,
        manager_name: branchData?.managerName,
        manager_email: branchData?.managerEmail,
        is_active: branchData?.isActive
      })
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id };
  },

  // ============================================================================
  // ORGANIZATION DEPARTMENTS
  // ============================================================================

  async getAllDepartments() {
    const { data, error } = await supabase
      ?.from('organization_departments')
      ?.select(`
        *,
        organization_branches (branch_name)
      `)
      ?.order('department_code', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      departmentCode: row?.department_code,
      departmentName: row?.department_name,
      branchId: row?.branch_id,
      branchName: row?.organization_branches?.branch_name,
      parentDepartmentId: row?.parent_department_id,
      costCenterCode: row?.cost_center_code,
      budgetCode: row?.budget_code,
      managerName: row?.manager_name,
      managerEmail: row?.manager_email,
      isActive: row?.is_active,
      createdAt: row?.created_at
    })) || [];
  },

  async createDepartment(deptData) {
    const { data, error } = await supabase
      ?.from('organization_departments')
      ?.insert({
        department_code: deptData?.departmentCode,
        department_name: deptData?.departmentName,
        branch_id: deptData?.branchId,
        parent_department_id: deptData?.parentDepartmentId,
        cost_center_code: deptData?.costCenterCode,
        budget_code: deptData?.budgetCode,
        manager_name: deptData?.managerName,
        manager_email: deptData?.managerEmail,
        is_active: deptData?.isActive
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id, departmentCode: data?.department_code };
  },

  // ============================================================================
  // PAYROLL SETTINGS
  // ============================================================================

  async getAllPayrollSettings() {
    const { data, error } = await supabase
      ?.from('payroll_settings')
      ?.select('*')
      ?.order('setting_category', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      settingKey: row?.setting_key,
      settingValue: row?.setting_value,
      settingCategory: row?.setting_category,
      description: row?.description,
      isActive: row?.is_active,
      createdAt: row?.created_at
    })) || [];
  },

  async updatePayrollSetting(id, settingData) {
    const { data, error } = await supabase
      ?.from('payroll_settings')
      ?.update({
        setting_value: settingData?.settingValue,
        description: settingData?.description,
        is_active: settingData?.isActive
      })
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id };
  },

  // ============================================================================
  // BANK CONFIGURATIONS
  // ============================================================================

  async getAllBankConfigurations() {
    const { data, error } = await supabase
      ?.from('bank_configurations')
      ?.select('*')
      ?.order('bank_name', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      bankName: row?.bank_name,
      bankCode: row?.bank_code,
      bankSwiftCode: row?.bank_swift_code,
      fileFormat: row?.file_format,
      transmissionMethod: row?.transmission_method,
      ftpHost: row?.ftp_host,
      ftpPort: row?.ftp_port,
      apiEndpoint: row?.api_endpoint,
      isActive: row?.is_active,
      createdAt: row?.created_at
    })) || [];
  },

  async updateBankConfiguration(id, bankData) {
    const { data, error } = await supabase
      ?.from('bank_configurations')
      ?.update({
        file_format: bankData?.fileFormat,
        transmission_method: bankData?.transmissionMethod,
        ftp_host: bankData?.ftpHost,
        ftp_port: bankData?.ftpPort,
        api_endpoint: bankData?.apiEndpoint,
        is_active: bankData?.isActive
      })
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id };
  },

  // ============================================================================
  // GL INTERFACE CONTROL
  // ============================================================================

  async getAllGLMappings() {
    const { data, error } = await supabase
      ?.from('gl_account_mappings')
      ?.select('*')
      ?.order('mapping_code', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      mappingCode: row?.mapping_code,
      mappingName: row?.mapping_name,
      sourceType: row?.source_type,
      sourceCode: row?.source_code,
      glAccountCode: row?.gl_account_code,
      glAccountName: row?.gl_account_name,
      costCenter: row?.cost_center,
      departmentCode: row?.department_code,
      isActive: row?.is_active,
      createdAt: row?.created_at
    })) || [];
  },

  async createGLExportBatch(payrollRunId, exportedBy) {
    try {
      const batchNumber = `GL-${new Date()?.toISOString()?.split('T')?.[0]?.replace(/-/g, '')}-${String(Math?.floor(Math?.random() * 1000))?.padStart(3, '0')}`;
      
      const { data, error } = await supabase
        ?.from('gl_export_batches')
        ?.insert({
          batch_number: batchNumber,
          payroll_run_id: payrollRunId,
          export_status: 'draft',
          exported_by: exportedBy
        })
        ?.select()
        ?.single();
      
      if (error) throw error;
      return { id: data?.id, batchNumber: data?.batch_number };
    } catch (error) {
      console.error('Error creating GL export batch:', error);
      throw error;
    }
  },

  // ============================================================================
  // EMPLOYEE LOANS
  // ============================================================================

  async getEmployeeLoans(employeeId) {
    const { data, error } = await supabase
      ?.from('employee_loans')
      ?.select('*')
      ?.eq('employee_id', employeeId)
      ?.order('start_date', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      loanNumber: row?.loan_number,
      loanType: row?.loan_type,
      loanAmount: row?.loan_amount,
      interestRate: row?.interest_rate,
      repaymentPeriodMonths: row?.repayment_period_months,
      monthlyDeduction: row?.monthly_deduction,
      totalRepaid: row?.total_repaid,
      balanceRemaining: row?.balance_remaining,
      loanStatus: row?.loan_status,
      startDate: row?.start_date,
      endDate: row?.end_date,
      approvedBy: row?.approved_by,
      approvedAt: row?.approved_at,
      notes: row?.notes,
      createdAt: row?.created_at
    })) || [];
  },

  async getAllLoans() {
    const { data, error } = await supabase
      ?.from('employee_loans')
      ?.select(`
        *,
        employees (employee_number, first_name, last_name, department)
      `)
      ?.order('start_date', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department
      } : null,
      loanNumber: row?.loan_number,
      loanType: row?.loan_type,
      loanAmount: row?.loan_amount,
      monthlyDeduction: row?.monthly_deduction,
      balanceRemaining: row?.balance_remaining,
      loanStatus: row?.loan_status,
      startDate: row?.start_date,
      createdAt: row?.created_at
    })) || [];
  },

  async createLoan(loanData) {
    const { data, error } = await supabase
      ?.from('employee_loans')
      ?.insert({
        employee_id: loanData?.employeeId,
        loan_number: loanData?.loanNumber,
        loan_type: loanData?.loanType,
        loan_amount: loanData?.loanAmount,
        interest_rate: loanData?.interestRate,
        repayment_period_months: loanData?.repaymentPeriodMonths,
        monthly_deduction: loanData?.monthlyDeduction,
        balance_remaining: loanData?.loanAmount,
        loan_status: 'active',
        start_date: loanData?.startDate,
        approved_by: loanData?.approvedBy,
        approved_at: new Date()?.toISOString(),
        notes: loanData?.notes
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id, loanNumber: data?.loan_number };
  },

  // ============================================================================
  // EMPLOYEE FLAGS
  // ============================================================================

  async getEmployeeFlags(employeeId) {
    const { data, error } = await supabase
      ?.from('employee_flags')
      ?.select('*')
      ?.eq('employee_id', employeeId)
      ?.order('flagged_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      flagType: row?.flag_type,
      flagReason: row?.flag_reason,
      flagStatus: row?.flag_status,
      severity: row?.severity,
      flaggedBy: row?.flagged_by,
      flaggedAt: row?.flagged_at,
      resolvedBy: row?.resolved_by,
      resolvedAt: row?.resolved_at,
      resolutionNotes: row?.resolution_notes,
      createdAt: row?.created_at
    })) || [];
  },

  async getAllFlags() {
    const { data, error } = await supabase
      ?.from('employee_flags')
      ?.select(`
        *,
        employees (employee_number, first_name, last_name, department)
      `)
      ?.eq('flag_status', 'active')
      ?.order('flagged_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department
      } : null,
      flagType: row?.flag_type,
      flagReason: row?.flag_reason,
      severity: row?.severity,
      flaggedBy: row?.flagged_by,
      flaggedAt: row?.flagged_at,
      createdAt: row?.created_at
    })) || [];
  },

  async createFlag(flagData) {
    const { data, error } = await supabase
      ?.from('employee_flags')
      ?.insert({
        employee_id: flagData?.employeeId,
        flag_type: flagData?.flagType,
        flag_reason: flagData?.flagReason,
        flag_status: 'active',
        severity: flagData?.severity,
        flagged_by: flagData?.flaggedBy
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id };
  },

  async resolveFlag(id, resolvedBy, resolutionNotes) {
    const { data, error } = await supabase
      ?.from('employee_flags')
      ?.update({
        flag_status: 'resolved',
        resolved_by: resolvedBy,
        resolved_at: new Date()?.toISOString(),
        resolution_notes: resolutionNotes
      })
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id };
  },

  // ============================================================================
  // EMPLOYEE HISTORICAL PAYS
  // ============================================================================

  async getEmployeeHistoricalPays(employeeId) {
    const { data, error } = await supabase
      ?.from('employee_historical_pays')
      ?.select('*')
      ?.eq('employee_id', employeeId)
      ?.order('payment_date', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      payrollRunId: row?.payroll_run_id,
      payPeriodStart: row?.pay_period_start,
      payPeriodEnd: row?.pay_period_end,
      paymentDate: row?.payment_date,
      grossPay: row?.gross_pay,
      netPay: row?.net_pay,
      totalDeductions: row?.total_deductions,
      totalAllowances: row?.total_allowances,
      swtAmount: row?.swt_amount,
      nasfundEmployee: row?.nasfund_employee,
      nasfundEmployer: row?.nasfund_employer,
      payslipNumber: row?.payslip_number,
      paymentMethod: row?.payment_method,
      paymentReference: row?.payment_reference,
      createdAt: row?.created_at
    })) || [];
  },

  // ============================================================================
  // REVERSE PAY REQUESTS
  // ============================================================================

  async getAllReversePayRequests() {
    const { data, error } = await supabase
      ?.from('reverse_pay_requests')
      ?.select(`
        *,
        payroll_runs (pay_period_start, pay_period_end, payment_date)
      `)
      ?.order('requested_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      requestNumber: row?.request_number,
      payrollRunId: row?.payroll_run_id,
      payrollRun: row?.payroll_runs ? {
        payPeriodStart: row?.payroll_runs?.pay_period_start,
        payPeriodEnd: row?.payroll_runs?.pay_period_end,
        paymentDate: row?.payroll_runs?.payment_date
      } : null,
      requestStatus: row?.request_status,
      reversalReason: row?.reversal_reason,
      affectedEmployees: row?.affected_employees,
      totalAmountReversed: row?.total_amount_reversed,
      requestedBy: row?.requested_by,
      requestedAt: row?.requested_at,
      approvedBy: row?.approved_by,
      approvedAt: row?.approved_at,
      processedBy: row?.processed_by,
      processedAt: row?.processed_at,
      rejectionReason: row?.rejection_reason,
      createdAt: row?.created_at
    })) || [];
  },

  async createReversePayRequest(requestData) {
    const requestNumber = `REV-${new Date()?.toISOString()?.split('T')?.[0]?.replace(/-/g, '')}-${String(Math?.floor(Math?.random() * 1000))?.padStart(3, '0')}`;
    
    const { data, error } = await supabase
      ?.from('reverse_pay_requests')
      ?.insert({
        request_number: requestNumber,
        payroll_run_id: requestData?.payrollRunId,
        request_status: 'pending',
        reversal_reason: requestData?.reversalReason,
        affected_employees: requestData?.affectedEmployees,
        total_amount_reversed: requestData?.totalAmountReversed,
        requested_by: requestData?.requestedBy
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id, requestNumber: data?.request_number };
  },

  async approveReversePayRequest(id, approvedBy) {
    const { data, error } = await supabase
      ?.from('reverse_pay_requests')
      ?.update({
        request_status: 'approved',
        approved_by: approvedBy,
        approved_at: new Date()?.toISOString()
      })
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id };
  },

  async rejectReversePayRequest(id, rejectionReason) {
    const { data, error } = await supabase
      ?.from('reverse_pay_requests')
      ?.update({
        request_status: 'rejected',
        rejection_reason: rejectionReason
      })
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id };
  },

  // ============================================================================
  // PURGE PAY REQUESTS
  // ============================================================================

  async getAllPurgePayRequests() {
    const { data, error } = await supabase
      ?.from('purge_pay_requests')
      ?.select(`
        *,
        payroll_runs (pay_period_start, pay_period_end, payment_date)
      `)
      ?.order('requested_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      requestNumber: row?.request_number,
      payrollRunId: row?.payroll_run_id,
      payrollRun: row?.payroll_runs ? {
        payPeriodStart: row?.payroll_runs?.pay_period_start,
        payPeriodEnd: row?.payroll_runs?.pay_period_end,
        paymentDate: row?.payroll_runs?.payment_date
      } : null,
      requestStatus: row?.request_status,
      purgeReason: row?.purge_reason,
      affectedRecords: row?.affected_records,
      dataRetentionPeriodDays: row?.data_retention_period_days,
      requestedBy: row?.requested_by,
      requestedAt: row?.requested_at,
      approvedBy: row?.approved_by,
      approvedAt: row?.approved_at,
      processedBy: row?.processed_by,
      processedAt: row?.processed_at,
      rejectionReason: row?.rejection_reason,
      archiveLocation: row?.archive_location,
      createdAt: row?.created_at
    })) || [];
  },

  async createPurgePayRequest(requestData) {
    const requestNumber = `PURGE-${new Date()?.toISOString()?.split('T')?.[0]?.replace(/-/g, '')}-${String(Math?.floor(Math?.random() * 1000))?.padStart(3, '0')}`;
    
    const { data, error } = await supabase
      ?.from('purge_pay_requests')
      ?.insert({
        request_number: requestNumber,
        payroll_run_id: requestData?.payrollRunId,
        request_status: 'pending',
        purge_reason: requestData?.purgeReason,
        affected_records: requestData?.affectedRecords,
        data_retention_period_days: requestData?.dataRetentionPeriodDays || 90,
        requested_by: requestData?.requestedBy
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id, requestNumber: data?.request_number };
  },

  async approvePurgePayRequest(id, approvedBy) {
    const { data, error } = await supabase
      ?.from('purge_pay_requests')
      ?.update({
        request_status: 'approved',
        approved_by: approvedBy,
        approved_at: new Date()?.toISOString()
      })
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id };
  },

  // ============================================================================
  // IMPORT PAY FROM FILE
  // ============================================================================

  async createImportSession(sessionData) {
    const { data, error } = await supabase
      ?.from('payroll_import_sessions')
      ?.insert({
        session_name: sessionData?.sessionName,
        import_source: sessionData?.importSource,
        file_name: sessionData?.fileName,
        file_url: sessionData?.fileUrl,
        import_status: 'pending',
        imported_by: sessionData?.importedBy
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { id: data?.id, sessionName: data?.session_name };
  },

  async getAllImportSessions() {
    const { data, error } = await supabase
      ?.from('payroll_import_sessions')
      ?.select('*')
      ?.order('imported_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      sessionName: row?.session_name,
      importSource: row?.import_source,
      fileName: row?.file_name,
      fileUrl: row?.file_url,
      importStatus: row?.import_status,
      totalRecords: row?.total_records,
      processedRecords: row?.processed_records,
      failedRecords: row?.failed_records,
      validationErrors: row?.validation_errors,
      importedBy: row?.imported_by,
      importedAt: row?.imported_at,
      completedAt: row?.completed_at,
      createdAt: row?.created_at
    })) || [];
  }
};

export default systemControlService;